import React, { JSX, useCallback, useEffect, useMemo, useState } from "react";
import "./DashboardReportWrapper.scss";
import { ComponentGroupGridLayoutWrapper } from "components/fragments/dashboard/components/layouts/grid/GridLayout";
import { DweenTimeUnitEnum } from "utils/enums";
import { Dashboard } from "api/model/dashboard/Dashboard";
import { BROWSER_ZONE_ID, fmtDatetime, JodaTimeUtils } from "utils/time-utils";
import {
  DashboardDateVariable,
  DashboardHtmlVariable,
  DashboardReportMarginBoxes,
  DashboardVariable,
} from "api/model/dashboard/DashboardReportOptions";
import { I, T } from "services/lang/LanguageService";
import {
  getDashboardComponentState,
  getDashboardState,
  getFinishedDashboardComponents,
  setDashboardState,
  storeDashboardComponentState,
} from "store/window-dashboard-store";
import { isDweenCalendarEnum } from "components/fragments/dashboard/utils";
import { DateTimeFormatter, Instant, ZonedDateTime, ZoneId } from "@js-joda/core";
import { DashboardLayoutType, IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import { ComponentGroupLayoutWrapper } from "../dashboard-report/DashboardReportPage";
import {
  DashboardComponentMappingWrapper,
  getFullComponentId,
} from "../dashboard/components/dashboard-utils";
import {
  DashboardComponentErrorState,
  DashboardComponentLoadingState,
  DashboardComponentState,
} from "../dashboard/components/interfaces";
import { getComponentType } from "../../ComponentType";
import truncateAndOffsetLocalDatetime = JodaTimeUtils.truncateAndOffsetLocalDatetime;

declare global {
  interface Window {
    reportGenerationOptions?: {
      companyRef: string;
      dashboardId: string;
      dashboardTitle: string;
      referenceTime: string;
      easasUrl: string;
      scheduleTime: string | null;
      genStartTime: string;
    };
  }
}

type ReportWrapperProps = {
  dashboard: Dashboard;
  referenceTime: string;
  children: JSX.Element | JSX.Element[];
};

type DashboardReportComponentProps = {
  header: string;
  isGrid?: boolean;
  children: JSX.Element;
};

const DashboardReportWrapper = (props: {
  dashboard: Dashboard;
  referenceTime: string;
  finishCallback: (status: "finished" | "error") => void;
  isDashboardLoaded: boolean;
  isInPreviewMode: boolean;
}) => {
  // All dashboard components with callback support
  const [errorList, setErrorList] = useState<DashboardComponentErrorState[]>([]);
  const [loadingList, setLoadingList] = useState<DashboardComponentLoadingState[]>([]);
  const supportedComponents = useMemo(() => {
    const components: Record<string, IDashboardComponent> = {};
    props.dashboard.dashboardTabsJs?.forEach((t, tabIndex) => {
      t.componentGroups?.forEach((g, groupIndex) => {
        g.components?.forEach((c, componentIndex) => {
          const typeMapped = getComponentType(c.type);
          if (typeMapped?.supportsStatusCallback) {
            const stringId = getFullComponentId(c.type, tabIndex, groupIndex, componentIndex);
            components[stringId] = c;
          }
        });
      });
    });
    return components;
  }, [props.dashboard.dashboardTabsJs]);

  useEffect(() => {
    const hasAnyComponents = Object.keys(supportedComponents).length > 0;
    if (!hasAnyComponents && getDashboardState() !== "finished") {
      console.log("No components in this tab — finishing immediately (once)");
      props.finishCallback("finished");
      setDashboardState("finished");
    }
  }, [supportedComponents]);

  useEffect(() => {
    setDashboardState("loading");
  }, [props.dashboard]);

  const componentIds = Object.keys(supportedComponents);

  // All dashboard components with callback support
  const componentCount = Object.keys(supportedComponents).length;

  // True if all supported components are finished
  const allFinished = () =>
    Object.keys(supportedComponents).every(id => {
      const state = getDashboardComponentState(id);
      return state?.state === "finished";
    }) || Object.keys(supportedComponents).length === 0;

  const componentStateCb = useCallback(
    (state: DashboardComponentState) => {
      storeDashboardComponentState(state);
      if (state.state === "loading") {
        setLoadingList(prev => (prev.some(l => l.id === state.id) ? prev : [...prev, state]));
      } else {
        setLoadingList(prev => prev.filter(l => l.id !== state.id));
      }

      // Currently finished components
      const finishedCount = getFinishedDashboardComponents(componentIds);

      // Calculate percentage of finished components
      const percent = Math.round((finishedCount / componentCount) * 100);
      console.log(
        `Status of dashboard: ${state.id} -> ${state.state} (${finishedCount}/${componentCount} = ${percent}% finished)`
      );

      // Short circuit if dashboard is already finished or errored
      if (getDashboardState() === "finished" || getDashboardState() === "error") return;

      // Check if component errored
      if (state.state === "error") {
        const currentErrors = [...errorList];
        setErrorList([...currentErrors, state]);
        props.finishCallback("error");
        setDashboardState("error");
      }

      // If all components are finished, set dashboard state to finished
      if (allFinished()) {
        props.finishCallback("finished");
        setDashboardState("finished");
      }
    },
    [props.dashboard]
  );

  return (
    <div className={`${props.isDashboardLoaded ? "" : "loading-dashboard"}`}>
      {errorList.length > 0 && !props.isInPreviewMode && (
        <div className="dashboard-report-error-dialog">
          <div className="error-dialog-header">{T("word.error")}</div>
          <ul className="error-dialog-list">
            {errorList.map(err => (
              <li key={err.id}>
                <div className="error-id">{err.id}</div>
                <div className="error-message">{err.error}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {loadingList.length > 0 && !props.isInPreviewMode && (
        <div className="dashboard-report-loading-dialog">
          <div className="loading-dialog-header">{T("word.loading")}</div>
          <ul className="loading-dialog-list">
            {loadingList.map(item => (
              <li key={item.id}>
                <div className="loading-id">{item.id}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <ReportWrapper dashboard={props.dashboard} referenceTime={props.referenceTime}>
        {props.dashboard.dashboardTabsJs?.map((tab, tabIdx) => (
          <div key={tab.uuid} className={tabIdx === 0 ? "" : "report-tab-break"}>
            {tab.componentGroups?.map((group, groupIdx) => {
              const isGridGroup = group.components?.some(c => c.layout?.[DashboardLayoutType.GRID]);

              if (isGridGroup) {
                return (
                  <ComponentGroupGridLayoutWrapper
                    key={group.id}
                    reportPageSize={props.dashboard?.reportOptions?.pageLayout.format?.pageSize}
                    group={group}
                    tabIdx={tabIdx}
                    groupIdx={groupIdx}
                    referenceTime={props.referenceTime}
                    componentStateCb={componentStateCb}
                    dashboard={props.dashboard}
                  />
                );
              }

              return (
                <ComponentGroupLayoutWrapper key={group.id} group={group}>
                  {group.components?.map((component, componentIdx) => (
                    <DashboardReportComponent key={component.id} header={I(component.title)}>
                      <DashboardComponentMappingWrapper
                        id={getFullComponentId(component.type, tabIdx, groupIdx, componentIdx)}
                        component={component.type}
                        config={component.config}
                        referenceTime={props.referenceTime}
                        stateCb={componentStateCb}
                        calendarType={
                          isDweenCalendarEnum(
                            props.dashboard?.reportOptions.timingOptions.referenceTimeTruncateTo
                          )
                            ? props.dashboard?.reportOptions.timingOptions.referenceTimeTruncateTo
                            : undefined
                        }
                      />
                    </DashboardReportComponent>
                  ))}
                </ComponentGroupLayoutWrapper>
              );
            })}
          </div>
        ))}
      </ReportWrapper>
    </div>
  );
};

const ReportWrapper = ({ children, dashboard, referenceTime }: ReportWrapperProps) => {
  const DEFAULT_PATTERN = "yyyy-MM-dd HH:mm";
  const { marginBoxes } = dashboard.reportOptions;

  const processVariables = (html: string) => {
    const pattern = dashboard.reportOptions.timingOptions.reportTimeFormat ?? DEFAULT_PATTERN;

    const scheduleTimeReplacer = () => {
      if (!window.reportGenerationOptions?.scheduleTime) return "NOT-SCHEDULED";
      return fmtDatetime(window.reportGenerationOptions?.scheduleTime, BROWSER_ZONE_ID, pattern);
    };

    const generationTimeReplacer = () => {
      if (!window.reportGenerationOptions?.genStartTime) return "NOT-GENERATED";
      return fmtDatetime(window.reportGenerationOptions?.genStartTime, BROWSER_ZONE_ID, pattern);
    };

    const referenceTimeReplacer = () => {
      return fmtDatetime(referenceTime, BROWSER_ZONE_ID, pattern);
    };

    const replacer: Record<string, string> = {
      scheduleTime: scheduleTimeReplacer(),
      referenceTime: referenceTimeReplacer(),
      generationTime: generationTimeReplacer(),
      totalPages: '<span class="page-number-total"></span>',
      currentPage: '<span class="page-number-current"></span>',
    };

    let processed = html;
    Object.keys(replacer).forEach(kv => {
      const value = replacer[kv];
      const regex = new RegExp(`\\$${kv}`, "g");
      processed = processed.replace(regex, value);
    });

    return processed;
  };

  const variableValueMapper = (value: DashboardVariable, currentDashboard: Dashboard) => {
    switch (value.type) {
      case "DATE": {
        const defaults: Partial<DashboardDateVariable> = {
          dateType: "GENERATION_TIME",
          truncateTo: DweenTimeUnitEnum.HOURS,
          offsetUnit: DweenTimeUnitEnum.HOURS,
          offset: 0,
          format: DEFAULT_PATTERN,
        };

        let dateObj = value as DashboardDateVariable;
        // apply defaults
        dateObj = { ...defaults, ...dateObj };

        const zoneId = ZoneId.of(currentDashboard.reportOptions.timingOptions.timeZoneId);
        let date: any;
        switch (dateObj.dateType) {
          case "GENERATION_TIME":
            date = ZonedDateTime.now(zoneId).toLocalDateTime();
            break;
          case "REFERENCE_TIME":
            date = Instant.parse(referenceTime).atZone(zoneId).toLocalDateTime();
            break;
          default:
            throw new Error(`Unsupported date type: ${dateObj.dateType}`);
        }

        date = truncateAndOffsetLocalDatetime(
          date,
          dateObj.truncateTo!!,
          dateObj.offset!!,
          dateObj.offsetUnit!!
        );

        return date.format(DateTimeFormatter.ofPattern(dateObj.format!!));
      }
      case "HTML": {
        const htmlObj = value as DashboardHtmlVariable;
        return htmlObj.content;
      }
      default:
        throw new Error(`Unsupported variable type: ${value.type}`);
    }
  };

  /**
   * Variables prefixed with $ are replaced with builtin values or values from dashboard.reportOptions.variables
   * @param marginBox margin box name to process
   */
  const preformatMarginBox = (marginBox: keyof DashboardReportMarginBoxes) => {
    const box = marginBoxes?.[marginBox];
    const html = box?.content;
    if (!html) return undefined;

    // Fixed variables processing
    // TODO: when there is more builtin values make replacer map struct to cleanup this code
    let processed = processVariables(html);

    // Margin box dynamic variables processing
    const boxVariables = box.variables;
    if (boxVariables) {
      Object.keys(boxVariables).forEach(kv => {
        const value = boxVariables[kv];
        const regex = new RegExp(`\\$${kv}`, "g");
        processed = processed.replace(regex, value);
      });
    }

    // Global variables processing
    const globalVariables = dashboard.reportOptions.variables;
    if (globalVariables) {
      Object.keys(globalVariables).forEach(kv => {
        const objValue = globalVariables[kv];
        const regex = new RegExp(`\\$${kv}`, "g");
        const value = variableValueMapper(objValue, dashboard);
        processed = processed.replace(regex, value);
      });
    }

    return { __html: I(processed) };
  };

  return (
    <div id="dashboard-report-wrapper-paged" className="dashboard-report-wrapper-paged print-mode-enabled">
      {/* Margin Boxes */}
      <div className="topLeftMarginBox" dangerouslySetInnerHTML={preformatMarginBox("topLeft")} />
      <div className="topCenterMarginBox" dangerouslySetInnerHTML={preformatMarginBox("topCenter")} />
      <div className="topRightMarginBox" dangerouslySetInnerHTML={preformatMarginBox("topRight")} />
      <div className="bottomLeftMarginBox" dangerouslySetInnerHTML={preformatMarginBox("bottomLeft")} />
      <div className="bottomCenterMarginBox" dangerouslySetInnerHTML={preformatMarginBox("bottomCenter")} />
      <div className="bottomRightMarginBox" dangerouslySetInnerHTML={preformatMarginBox("bottomRight")} />

      {/* Dashboad Components */}
      {children}
    </div>
  );
};

const useBorders = (componentType: string, theme?: string) => {
  if (theme === "dark" && componentType !== "FreeText" && componentType !== "Separator") {
    return "component-border";
  }
  return "";
};

export const DashboardReportComponent = ({ header, isGrid, children }: DashboardReportComponentProps) => {
  return (
    <div
      className={`dashboard-report-component ${isGrid ? "h-full" : ""} ${children.props.config.theme ?? "light"} ${useBorders(children.props.component, children.props.config.theme)} `}
    >
      {header && (
        <div className="dashboard-report-component-header">
          <div className="m-3">{I(header)}</div>
        </div>
      )}

      <div className="dashboard-report-component-body">{React.cloneElement(children)}</div>
    </div>
  );
};

export default DashboardReportWrapper;
